/**
 * D27 Nakshatramsha (Bhamsha) Division Attributes Data
 * Based on /Users/vn/src/hora/docs/new/bhamsha_nakshatramsha_table.md
 * 27 divisions per rashi (30° / 27 = 1°06'40" each)
 */

import { JatakaResponse } from '../../../../../models/api';
import { getRashiRuler } from '../../../../../utils/astrology/calculations';

export interface NakshatramshaAttributes {
  amsha: number;
  nakshatra: string;
  deity: string; // Bhamsha deity
  degreeStart: string;
  degreeEnd: string;
}

/**
 * 27 Nakshatramsha divisions with their attributes
 * Each division = 1°06'40" (1.111...°)
 */
export const NAKSHATRAMSHA_ATTRIBUTES: NakshatramshaAttributes[] = [
  { amsha: 1, nakshatra: "Ashvini", deity: "Ashvini Kumary", degreeStart: "0°00'00\"", degreeEnd: "1°06'40\"" },
  { amsha: 2, nakshatra: "Bharani", deity: "Yama", degreeStart: "1°06'40\"", degreeEnd: "2°13'20\"" },
  { amsha: 3, nakshatra: "Krittika", deity: "Agni", degreeStart: "2°13'20\"", degreeEnd: "3°20'00\"" },
  { amsha: 4, nakshatra: "Rohini", deity: "Brahma", degreeStart: "3°20'00\"", degreeEnd: "4°26'40\"" },
  { amsha: 5, nakshatra: "Mrigashira", deity: "Chandra", degreeStart: "4°26'40\"", degreeEnd: "5°33'20\"" },
  { amsha: 6, nakshatra: "Ardra", deity: "Rudra", degreeStart: "5°33'20\"", degreeEnd: "6°40'00\"" },
  { amsha: 7, nakshatra: "Punarvasu", deity: "Aditi", degreeStart: "6°40'00\"", degreeEnd: "7°46'40\"" },
  { amsha: 8, nakshatra: "Pushya", deity: "Brihaspati", degreeStart: "7°46'40\"", degreeEnd: "8°53'20\"" },
  { amsha: 9, nakshatra: "Ashlesha", deity: "Sarpa", degreeStart: "8°53'20\"", degreeEnd: "10°00'00\"" },
  { amsha: 10, nakshatra: "Magha", deity: "Pitri", degreeStart: "10°00'00\"", degreeEnd: "11°06'40\"" },
  { amsha: 11, nakshatra: "Purva Phalguni", deity: "Bhaga", degreeStart: "11°06'40\"", degreeEnd: "12°13'20\"" },
  { amsha: 12, nakshatra: "Uttara Phalguni", deity: "Aryama", degreeStart: "12°13'20\"", degreeEnd: "13°20'00\"" },
  { amsha: 13, nakshatra: "Hasta", deity: "Savitri", degreeStart: "13°20'00\"", degreeEnd: "14°26'40\"" },
  { amsha: 14, nakshatra: "Chitra", deity: "Tvashtra", degreeStart: "14°26'40\"", degreeEnd: "15°33'20\"" },
  { amsha: 15, nakshatra: "Svati", deity: "Vayu", degreeStart: "15°33'20\"", degreeEnd: "16°40'00\"" },
  { amsha: 16, nakshatra: "Vishakha", deity: "Indra-Agni", degreeStart: "16°40'00\"", degreeEnd: "17°46'40\"" },
  { amsha: 17, nakshatra: "Anuradha", deity: "Mitra", degreeStart: "17°46'40\"", degreeEnd: "18°53'20\"" },
  { amsha: 18, nakshatra: "Jyeshtha", deity: "Indra", degreeStart: "18°53'20\"", degreeEnd: "20°00'00\"" },
  { amsha: 19, nakshatra: "Mula", deity: "Nirriti", degreeStart: "20°00'00\"", degreeEnd: "21°06'40\"" },
  { amsha: 20, nakshatra: "Purva Ashadha", deity: "Apas", degreeStart: "21°06'40\"", degreeEnd: "22°13'20\"" },
  { amsha: 21, nakshatra: "Uttara Ashadha", deity: "Vishva-deva", degreeStart: "22°13'20\"", degreeEnd: "23°20'00\"" },
  { amsha: 22, nakshatra: "Shravana", deity: "Vishnu", degreeStart: "23°20'00\"", degreeEnd: "24°26'40\"" },
  { amsha: 23, nakshatra: "Dhanishtha", deity: "Vasu", degreeStart: "24°26'40\"", degreeEnd: "25°33'20\"" },
  { amsha: 24, nakshatra: "Shatabhisha", deity: "Varuna", degreeStart: "25°33'20\"", degreeEnd: "26°40'00\"" },
  { amsha: 25, nakshatra: "Purva Bhadra", deity: "Aja Ekapada", degreeStart: "26°40'00\"", degreeEnd: "27°46'40\"" },
  { amsha: 26, nakshatra: "Uttara Bhadra", deity: "Ahirbudhnya", degreeStart: "27°46'40\"", degreeEnd: "28°53'20\"" },
  { amsha: 27, nakshatra: "Revati", deity: "Pushan", degreeStart: "28°53'20\"", degreeEnd: "30°00'00\"" }
];


/**
 * D27 Bhamsha mapping table for all 12 rashis
 * Each row = one amsha (1-27), each column = one rashi (0-11)
 * Values = resulting rashi number (1-12)
 */
export const D27_RASHI_MAPPING: number[][] = [
  // Amsha 1-27, Rashis: Mesha(0), Vrisha(1), Mithuna(2), Karkata(3), Simha(4), Kanya(5), Tula(6), Vrishchika(7), Dhanu(8), Makara(9), Kumbha(10), Meena(11)
  [1, 4, 7, 10, 1, 4, 7, 10, 1, 4, 7, 10],  // 1. Ashvini (Dastra)
  [2, 5, 8, 11, 2, 5, 8, 11, 2, 5, 8, 11],  // 2. Bharani (Yama)
  [3, 6, 9, 12, 3, 6, 9, 12, 3, 6, 9, 12],  // 3. Krittika (Agni)
  [4, 7, 10, 1, 4, 7, 10, 1, 4, 7, 10, 1],  // 4. Rohini (Brahma)
  [5, 8, 11, 2, 5, 8, 11, 2, 5, 8, 11, 2],  // 5. Mrigashira (Chandra)
  [6, 9, 12, 3, 6, 9, 12, 3, 6, 9, 12, 3],  // 6. Ardra (Isha)
  [7, 10, 1, 4, 7, 10, 1, 4, 7, 10, 1, 4],  // 7. Punarvasu (Aditi)
  [8, 11, 2, 5, 8, 11, 2, 5, 8, 11, 2, 5],  // 8. Pushya (Jiva)
  [9, 12, 3, 6, 9, 12, 3, 6, 9, 12, 3, 6],  // 9. Ashlesha (Ahi)
  [10, 1, 4, 7, 10, 1, 4, 7, 10, 1, 4, 7],  // 10. Magha (Pitara)
  [11, 2, 5, 8, 11, 2, 5, 8, 11, 2, 5, 8],  // 11. Purva Phalguni (Bhaga)
  [12, 3, 6, 9, 12, 3, 6, 9, 12, 3, 6, 9],  // 12. Uttara Phalguni (Aryama)
  [1, 4, 7, 10, 1, 4, 7, 10, 1, 4, 7, 10],  // 13. Hasta (Surya)
  [2, 5, 8, 11, 2, 5, 8, 11, 2, 5, 8, 11],  // 14. Chitra (Tvashta)
  [3, 6, 9, 12, 3, 6, 9, 12, 3, 6, 9, 12],  // 15. Svati (Maruta)
  [4, 7, 10, 1, 4, 7, 10, 1, 4, 7, 10, 1],  // 16. Vishakha (Chakragni)
  [5, 8, 11, 2, 5, 8, 11, 2, 5, 8, 11, 2],  // 17. Anuradha (Mitra)
  [6, 9, 12, 3, 6, 9, 12, 3, 6, 9, 12, 3],  // 18. Jyeshtha (Vasava)
  [7, 10, 1, 4, 7, 10, 1, 4, 7, 10, 1, 4],  // 19. Mula (Rakshasa)
  [8, 11, 2, 5, 8, 11, 2, 5, 8, 11, 2, 5],  // 20. Purva Ashadha (Varuna)
  [9, 12, 3, 6, 9, 12, 3, 6, 9, 12, 3, 6],  // 21. Uttara Ashadha (Vishva-deva)
  [10, 1, 4, 7, 10, 1, 4, 7, 10, 1, 4, 7],  // 22. Shravana (Govinda)
  [11, 2, 5, 8, 11, 2, 5, 8, 11, 2, 5, 8],  // 23. Dhanishtha (Vasu)
  [12, 3, 6, 9, 12, 3, 6, 9, 12, 3, 6, 9],  // 24. Shatabhisha (Varuna)
  [1, 4, 7, 10, 1, 4, 7, 10, 1, 4, 7, 10],  // 25. Purva Bhadra (Ajapata)
  [2, 5, 8, 11, 2, 5, 8, 11, 2, 5, 8, 11],  // 26. Uttara Bhadra (Ahir-Buddhanya)
  [3, 6, 9, 12, 3, 6, 9, 12, 3, 6, 9, 12]   // 27. Revati (Pusha)
];

/**
 * Parse longitude from position string format "rasi|degrees|longitude|speed"
 */
export function parseLongitudeFromPosition(position: string): number {
  const parts = position.split('|');
  if (parts.length >= 3) {
    return parseFloat(parts[2]);
  }
  return 0;
}

/**
 * Parse rashi index from position string format "rasi|degrees|longitude|speed"
 * Returns 0-11 (Mesha to Meena)
 * IMPORTANT: API returns 0-based index (0-11), not 1-based number (1-12)
 */
export function parseRashiFromPosition(position: string): number {
  const parts = position.split('|');
  if (parts.length >= 1) {
    const rashiIndex = parseInt(parts[0], 10);
    // API already returns 0-based index (0=Mesha, 11=Meena)
    return rashiIndex;
  }
  return 0;
}

/**
 * Calculate D27 Nakshatramsha amsha number (1-27) within a rashi
 * Formula: floor(degrees_in_rashi / 1.111111) + 1
 */
export function calculateNakshatramshaAmsha(degreesInRashi: number): number {
  const AMSHA_SIZE = 30 / 27; // 1.111111...
  const amshaIndex = Math.floor(degreesInRashi / AMSHA_SIZE);
  return Math.min(Math.max(amshaIndex + 1, 1), 27); // Clamp to 1-27
}

/**
 * Get resulting Bhamsha rashi for a planet
 * @param rashiIndex - 0-11 (Mesha to Meena)
 * @param amshaNumber - 1-27 (amsha within rashi)
 * @returns Resulting rashi number (1-12)
 */
export function getBhamshaRashi(rashiIndex: number, amshaNumber: number): number {
  if (rashiIndex < 0 || rashiIndex > 11 || amshaNumber < 1 || amshaNumber > 27) {
    return 1; // Default to Mesha
  }
  return D27_RASHI_MAPPING[amshaNumber - 1][rashiIndex];
}

/**
 * Planet D27 data
 */
export interface PlanetD27Data {
  shortName: string;
  degreesInRashi: number;
  amshaNumber: number;
  nakshatra: string;
  bhamshaRashiRuler: string; // Ruler of the bhamsha rashi (e.g., house 8 = Vrishchika → Ma)
  deity: string;
  bhamshaRashi: number; // House number 1-12 (represents which rashi in D27)
  rashiIndex: number;
  isRetrograde: boolean;
  isHora: boolean;
  isChandra: boolean;
  isLagna: boolean;
}

/**
 * Calculate D27 data for all planets
 */
export function calculateNakshatramshaData(apiResponse: JatakaResponse): PlanetD27Data[] {
  const planets: PlanetD27Data[] = [];

  if (!apiResponse?.graha_positions?.planets) {
    return planets;
  }

  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  apiResponse.graha_positions.planets.forEach(planet => {
    const longitude = parseLongitudeFromPosition(planet.position);
    const rashiIndex = parseRashiFromPosition(planet.position);
    const degreesInRashi = longitude % 30;

    const amshaNumber = calculateNakshatramshaAmsha(degreesInRashi);
    const amshaData = NAKSHATRAMSHA_ATTRIBUTES[amshaNumber - 1];
    const bhamshaRashiNumber = getBhamshaRashi(rashiIndex, amshaNumber);

    // Get ruler of the bhamsha rashi (house number → rashi index → ruler)
    // bhamshaRashiNumber is 1-12, convert to 0-11 for getRashiRuler
    const bhamshaRashiRuler = getRashiRuler(bhamshaRashiNumber - 1);

    const isLagna = planet.short_name === 'Lg';
    const isChandra = planet.short_name === 'Ch';
    const isRahuKetu = planet.short_name === 'Ra' || planet.short_name === 'Ke';
    const isRetrograde = !isRahuKetu && (planet.is_retrograde || false);

    const horaPlanetName = currentHoraPlanet ? currentHoraPlanet.split(' ')[0] : null;
    const isHora = !!(horaPlanetName && (
      planet.short_name === horaPlanetName ||
      planet.name === horaPlanetName
    ));

    planets.push({
      shortName: planet.short_name,
      degreesInRashi,
      amshaNumber,
      nakshatra: amshaData.nakshatra,
      bhamshaRashiRuler,
      deity: amshaData.deity,
      bhamshaRashi: bhamshaRashiNumber,
      rashiIndex,
      isRetrograde,
      isHora,
      isChandra,
      isLagna
    });
  });

  return planets;
}
